import React, { ComponentProps, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { BodyText, Icon, theme, useHovered, useShareTextDialog, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderShareActions = (props: Props) => {
  const { onShareEmail, onShareMessage, onShareMore } = props;

  const { formatMessage } = useIntl();
  const { canShare } = useShareTextDialog();

  // ─────────────────────────────────────────────────────────────────────

  const shareActions = useMemo(() => {
    if (canShare) {
      return [
        { iconName: 'IconEmail2', label: formatMessage(messages.email), onPress: onShareEmail },
        { iconName: 'IconMessage2', label: formatMessage(messages.message), onPress: onShareMessage }, // prettier-ignore
        { iconName: 'IconDots', label: formatMessage(messages.more), onPress: onShareMore },
      ] satisfies ShareActions[];
    }

    return [
      { iconName: 'IconEmail2', label: formatMessage(messages.email), onPress: onShareEmail },
      { iconName: 'IconMessage2', label: formatMessage(messages.message), onPress: onShareMessage },
    ] satisfies ShareActions[];
  }, [canShare, formatMessage, onShareEmail, onShareMessage, onShareMore]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <BodyText sizeMatch={['12']} style={styles.shareViaLabel}>
        <FormattedMessage {...messages.shareViaLabel} />
      </BodyText>

      <View style={styles.shareActions}>
        {shareActions.map((action) => (
          <ShareActionButton key={action.label} action={action} />
        ))}
      </View>
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ShareActionButton = (props: ShareActionButtonProps) => {
  const { action } = props;

  const shareActionRef = useRef(null);
  const isHovered = useHovered(shareActionRef);

  // ─── Styles ──────────────────────────────────────────────────────────

  const backgroundStyles = [
    styles.shareActionIconBg,
    shareActionIconBgWebStyles,
    isHovered ? styles.shareActionIconBgHovered : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={shareActionRef}
      accessibilityRole="button"
      accessibilityLabel={action.label}
      onPress={action.onPress}
      style={styles.shareAction}
      activeOpacity={0.75}
    >
      <View style={styles.shareActionIcon}>
        <View style={backgroundStyles} />
        <Icon name={action.iconName} />
      </View>

      <BodyText sizeMatch={['14']}>{action.label}</BodyText>
    </TouchableOpacity>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  shareViaLabel: {
    defaultMessage: 'OR SHARE VIA',
    description: 'Group Order Share | Modal | Share via label',
  },
  email: {
    defaultMessage: 'Email',
    description: 'Group Order Share | Modal | Email share label',
  },
  message: {
    defaultMessage: 'Message',
    description: 'Group Order Share | Modal | Message share label',
  },
  more: {
    defaultMessage: 'More',
    description: 'Group Order Share | Modal | More share label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['5'],
    marginBottom: theme.spacing['3'],
  },
  shareViaLabel: {
    color: theme.colors.CHARCOAL,
  },
  shareActions: {
    flexDirection: 'row',
    gap: theme.spacing['6'],
  },
  shareAction: {
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  shareActionIcon: {
    width: 56,
    height: 56,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareActionIconBg: {
    ...StyleSheet.absoluteFill,
    borderRadius: 56,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  shareActionIconBgHovered: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

const shareActionIconBgWebStyles = webOnlyStyles({
  transition: `background-color ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  onShareEmail: () => void;
  onShareMessage: () => void;
  onShareMore: () => void;
};

type ShareActions = {
  iconName: IconName;
  label: string;
  onPress: () => void;
};

type ShareActionButtonProps = {
  action: ShareActions;
};

type IconName = ComponentProps<typeof Icon>['name'];
